import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  UserCircle, CurrencyEur, CreditCard, Receipt, FilePdf,
  CalendarBlank, ArrowsClockwise, Hourglass, Play,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const STATUS_MAP = {
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700' },
  trialing: { label: 'Essai', color: 'bg-blue-100 text-blue-700' },
  trial_free: { label: 'Essai gratuit', color: 'bg-sky-100 text-sky-700' },
  grace_period: { label: 'Grace', color: 'bg-amber-100 text-amber-700' },
  cancelled: { label: 'Annule', color: 'bg-gray-200 text-gray-600' },
  expired: { label: 'Expire', color: 'bg-red-100 text-red-700' },
  no_subscription: { label: 'Aucun', color: 'bg-gray-100 text-gray-500' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] || STATUS_MAP.no_subscription;
  return <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-bold ${cfg.color}`}>{cfg.label}</span>;
}

function getSubCardBorder(status) {
  if (status === 'active') return 'border-emerald-200 bg-emerald-50/30';
  if (status === 'trialing') return 'border-blue-200 bg-blue-50/30';
  if (status === 'trial_free') return 'border-sky-200 bg-sky-50/30';
  if (status === 'grace_period') return 'border-amber-200 bg-amber-50/30';
  return 'border-gray-200 bg-gray-50/30';
}

function getPaymentBadge(status) {
  if (status === 'paid') return 'bg-emerald-100 text-emerald-700';
  return 'bg-amber-100 text-amber-700';
}

function getPaymentLabel(status) {
  if (status === 'paid') return 'Paye';
  if (status === 'initiated') return 'En cours';
  return status;
}

export function AdminMemberDetail({
  open, onOpenChange, memberDetail, loadingDetail,
  plans, simPlanId, setSimPlanId, simulating,
  onSimulate, downloadInvoice,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" aria-describedby="member-detail-desc">
        <SheetHeader>
          <SheetTitle>Detail du membre</SheetTitle>
          <p id="member-detail-desc" className="text-sm text-[#4B5563]">Abonnement, paiements et factures</p>
        </SheetHeader>

        {loadingDetail && !memberDetail && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" />
          </div>
        )}

        {memberDetail && (
          <div className="space-y-5 mt-4" data-testid="member-detail-panel">
            {/* User identity */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {memberDetail.member.picture ? (
                  <img src={memberDetail.member.picture} alt="" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <UserCircle size={28} className="text-gray-400" weight="fill" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-[#111827] truncate">{memberDetail.member.name || 'Sans nom'}</p>
                <p className="text-xs text-[#6B7280] truncate">{memberDetail.member.email}</p>
                <p className="text-[10px] text-[#9CA3AF]">
                  Inscrit le {memberDetail.member.created_at ? new Date(memberDetail.member.created_at).toLocaleDateString('fr-FR') : '—'}
                </p>
              </div>
            </div>

            {/* Subscription card */}
            <Card className={`border-2 ${getSubCardBorder(memberDetail.subscription.status)}`} data-testid="member-sub-card">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-sm text-[#111827]">Abonnement</h4>
                  <StatusBadge status={memberDetail.subscription.status} />
                </div>
                {memberDetail.subscription.plan ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <CurrencyEur size={16} className="text-[#FF6B00]" />
                      <span className="font-bold text-[#FF6B00]">{memberDetail.subscription.plan.name}</span>
                      <span className="text-[#6B7280]">— {memberDetail.subscription.plan.price} EUR</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-[#6B7280]">
                      {memberDetail.subscription.start_date && (
                        <div className="flex items-center gap-1.5"><CalendarBlank size={13} /><span>Debut : {new Date(memberDetail.subscription.start_date).toLocaleDateString('fr-FR')}</span></div>
                      )}
                      {memberDetail.subscription.end_date && (
                        <div className="flex items-center gap-1.5"><CalendarBlank size={13} /><span>Fin : {new Date(memberDetail.subscription.end_date).toLocaleDateString('fr-FR')}</span></div>
                      )}
                      {memberDetail.subscription.trial_end && (
                        <div className="flex items-center gap-1.5"><Hourglass size={13} /><span>Essai jusqu'au : {new Date(memberDetail.subscription.trial_end).toLocaleDateString('fr-FR')}</span></div>
                      )}
                      {memberDetail.subscription.auto_renew !== undefined && (
                        <div className="flex items-center gap-1.5"><ArrowsClockwise size={13} /><span>Renouvellement : {memberDetail.subscription.auto_renew ? 'Oui' : 'Non'}</span></div>
                      )}
                    </div>
                    {memberDetail.subscription.cancelled_at && (
                      <p className="text-[10px] text-red-500">Annule le {new Date(memberDetail.subscription.cancelled_at).toLocaleDateString('fr-FR')}</p>
                    )}
                    {memberDetail.subscription.next_payment && (
                      <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-2" data-testid="next-payment">
                        <CreditCard size={14} className="text-[#FF6B00]" />
                        <span className="text-xs font-semibold text-[#111827]">Prochain paiement : {new Date(memberDetail.subscription.next_payment).toLocaleDateString('fr-FR')}</span>
                        <span className="text-xs text-[#6B7280]">({memberDetail.subscription.plan?.price} EUR)</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-[#6B7280]">Aucun abonnement actif.</p>
                )}
              </CardContent>
            </Card>

            {/* Simulate status */}
            <Card className="border border-dashed border-orange-200 bg-orange-50/30" data-testid="simulate-section">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Play size={16} className="text-[#FF6B00]" weight="fill" />
                  <h4 className="font-bold text-sm text-[#111827]">Simuler un statut</h4>
                  <span className="text-[9px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-bold">TEST</span>
                </div>
                <p className="text-[10px] text-[#6B7280]">Changer le statut et le plan de ce membre pour tester l'application.</p>
                <div>
                  <Label className="text-xs text-[#6B7280]">Plan a simuler</Label>
                  <select value={simPlanId} onChange={e => setSimPlanId(e.target.value)}
                    className="w-full h-9 mt-1 rounded-md border border-gray-200 px-3 text-xs font-medium text-[#374151] bg-white"
                    data-testid="simulate-plan-select">
                    {plans.map(p => <option key={p.id} value={p.id}>{p.name} — {p.price} EUR</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { status: 'active', label: 'Actif', color: 'bg-emerald-600 hover:bg-emerald-700' },
                    { status: 'trialing', label: 'Essai', color: 'bg-blue-600 hover:bg-blue-700' },
                    { status: 'expired', label: 'Expire', color: 'bg-red-500 hover:bg-red-600' },
                    { status: 'cancelled', label: 'Annule', color: 'bg-gray-500 hover:bg-gray-600' },
                    { status: 'grace_period', label: 'Grace', color: 'bg-amber-500 hover:bg-amber-600' },
                    { status: 'none', label: 'Supprimér', color: 'bg-gray-800 hover:bg-gray-900' },
                  ].map(s => (
                    <Button key={s.status} size="sm"
                      onClick={() => onSimulate(memberDetail.member.id, s.status, simPlanId)}
                      disabled={simulating}
                      className={`h-9 text-xs text-white font-bold ${s.color} ${memberDetail.subscription.status === s.status ? 'ring-2 ring-offset-1 ring-[#FF6B00] opacity-80' : ''}`}
                      data-testid={`simulate-${s.status}`}>
                      {simulating ? <Loader2 className="h-3 w-3 animate-spin" /> : s.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payment history */}
            <div data-testid="member-payments">
              <h4 className="font-bold text-sm text-[#111827] mb-2 flex items-center gap-2">
                <CreditCard size={16} className="text-[#FF6B00]" /> Historique des paiements
              </h4>
              {(!memberDetail.payments || memberDetail.payments.length === 0) ? (
                <p className="text-xs text-[#9CA3AF] py-2">Aucun paiement enregistre.</p>
              ) : (
                <div className="space-y-2">
                  {memberDetail.payments.map(p => (
                    <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-[#111827]">{p.metadata?.plan_name || 'Paiement'}</p>
                        <p className="text-[10px] text-[#6B7280]">{new Date(p.created_at).toLocaleDateString('fr-FR')} — {getPaymentLabel(p.payment_status)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#111827]">{p.amount} <span className="text-[10px] text-[#9CA3AF]">EUR</span></p>
                        <Badge className={`text-[9px] ${getPaymentBadge(p.payment_status)}`}>{getPaymentLabel(p.payment_status)}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Invoices */}
            <div data-testid="member-invoices">
              <h4 className="font-bold text-sm text-[#111827] mb-2 flex items-center gap-2">
                <Receipt size={16} className="text-[#FF6B00]" /> Factures
              </h4>
              {(!memberDetail.invoices || memberDetail.invoices.length === 0) ? (
                <p className="text-xs text-[#9CA3AF] py-2">Aucune facture disponible.</p>
              ) : (
                <div className="space-y-2">
                  {memberDetail.invoices.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-[#111827]">{inv.invoice_number}</p>
                        <p className="text-[10px] text-[#6B7280]">{inv.plan_name} — {new Date(inv.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-[#111827]">{inv.amount} EUR</span>
                        <Button variant="outline" size="sm"
                          onClick={(e) => { e.stopPropagation(); downloadInvoice(inv.id, inv.invoice_number); }}
                          className="h-8 text-[#FF6B00]" data-testid={`member-dl-invoice-${inv.id}`}>
                          <FilePdf size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
